import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Database,
  Sparkles,
  UploadCloud,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectItem } from '../components/ui/select';
import { getAssignedAgents, isPositiveAgentSeat } from '../lib/access';
import axiosInstance from '../../axiosInstance';
import configData from '../../config';

const STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Dubai',
];

const INDUSTRIES = [
  'Automotive',
  'Construction',
  'Engineering',
  'Food Services',
  'Health Care',
  'Mining and Logistics',
  'Technology',
  'Telecommunication',
  'Paper Industry',
  'Service Industry',
];

const STEPS = [
  { id: 1, title: 'Org Profile', icon: Building2, desc: 'Complete your company profile' },
  { id: 2, title: 'Data Sources', icon: Database, desc: 'Connect ERP, CRM, HRIS, ITSM' },
  { id: 3, title: 'KPI Selection', icon: Sparkles, desc: 'Pick the metrics that matter' },
];

const SOURCES = [
  { id: 'sap', name: 'SAP S/4HANA', category: 'ERP' },
  { id: 'oracle', name: 'Oracle Fusion', category: 'ERP' },
  { id: 'salesforce', name: 'Salesforce', category: 'CRM' },
  { id: 'hubspot', name: 'HubSpot', category: 'CRM' },
  { id: 'workday', name: 'Workday', category: 'HRIS' },
  { id: 'bamboo', name: 'BambooHR', category: 'HRIS' },
  { id: 'servicenow', name: 'ServiceNow', category: 'ITSM' },
  { id: 'jira', name: 'Jira Service Mgmt', category: 'ITSM' },
];

const KPI_OPTIONS = [
  { id: 'finance', label: 'Finance - Revenue, margin, AR/AP' },
  { id: 'sales', label: 'Sales - Pipeline, win-rate, ACV' },
  { id: 'operations', label: 'Operations - SLA, uptime, incidents' },
  { id: 'hr', label: 'Human Capital - Headcount, attrition' },
  { id: 'supply', label: 'Supply Chain - Lead time, inventory' },
  { id: 'customer', label: 'Customer - NPS, churn, CSAT' },
];

export function OnboardingPage() {
  const account = useSelector((state) => state.account || {});
  const [step, setStep] = useState(1);
  const [org, setOrg] = useState({
    name: '',
    industry: '',
    address: '',
    city: '',
    state: '',
    email: '',
    phone: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgMessage, setOrgMessage] = useState('');
  const [sources, setSources] = useState([]);
  const [kpis, setKpis] = useState(['finance', 'sales', 'operations']);

  useEffect(() => {
    const orgData = account.org || account.org_data?.data || {};
    setOrg({
      name: orgData.name || '',
      industry: orgData.industry || '',
      address: orgData.address || '',
      city: orgData.city || '',
      state: orgData.state || '',
      email: orgData.email || account.user?.email || '',
      phone: orgData.phone || account.user?.phone_number || '',
    });
  }, [account]);

  const progress = (step / STEPS.length) * 100;

  const toggle = (id, list, setter) => {
    setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const updateOrg = (field, value) => {
    setOrg((current) => ({ ...current, [field]: value }));
  };

  const saveOrgProfile = async () => {
    setOrgSaving(true);
    setOrgMessage('');
    try {
      const userEmail = account.user?.email || account.org_member?.email || org.email || '';
      const allocationPayload = getOnboardingAllocations(account);
      const formData = new FormData();
      Object.entries(org).forEach(([key, value]) => formData.append(key, value || ''));
      if (logoFile) {
        formData.append('file', logoFile);
      }
      formData.append('user', userEmail);
      formData.append('organizationName', org.name || '');
      formData.append('organizationEmail', org.email || '');
      formData.append('organizationAddress', org.address || '');
      formData.append('pending_license_allocation', JSON.stringify({ allocations: allocationPayload }));
      formData.append('assigned_agents', JSON.stringify(allocationPayload));
      formData.append('allocations', JSON.stringify(allocationPayload));
      Object.entries(allocationPayload).forEach(([code, seats]) => {
        formData.append(code, seats);
        formData.append(`pending_license_allocation.allocations.${code}`, seats);
        formData.append(`pending_license_allocation[allocations][${code}]`, seats);
        formData.append(`allocations.${code}`, seats);
        formData.append(`allocations[${code}]`, seats);
      });

      const response = await axiosInstance.post(`${configData.API_SERVER}/organization/updateOrgDetails/`, formData);
      setOrgMessage(response?.data?.message || 'Organization profile saved.');
      return true;
    } catch (error) {
      console.error('Error saving organization profile', error);
      setOrgMessage(error.response?.data?.message || 'Unable to save organization profile.');
      return false;
    } finally {
      setOrgSaving(false);
    }
  };

  const canAdvance =
    (step === 1 && org.name && org.industry && org.address && org.city && org.state && org.email && org.phone) ||
    (step === 2 && sources.length > 0) ||
    (step === 3 && kpis.length > 0);

  const nextStep = async () => {
    if (step === 1) {
      const saved = await saveOrgProfile();
      if (!saved) return;
    }
    setStep(step + 1);
  };

  const finish = () => {
    window.location.href = '/imagei/overview';
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex flex-col">
      <div className="relative overflow-hidden border-b border-border/60">
        <div aria-hidden className="absolute inset-0 -z-10 opacity-80" style={{ backgroundImage: 'var(--gradient-aurora)' }} />
        <div className="px-6 lg:px-10 py-8 max-w-5xl mx-auto w-full">
          <p className="text-[11px] uppercase tracking-[0.22em] text-primary">Welcome to IMAGE-I</p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-2">
            Let&apos;s set up your reporting workspace
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Complete your organization profile, choose data sources, and select KPI groups.
          </p>

          <div className="mt-6 flex items-center gap-3">
            <Progress value={progress} className="h-1.5 flex-1" />
            <span className="text-xs text-muted-foreground tabular-nums">Step {step} of {STEPS.length}</span>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {STEPS.map((item) => {
              const active = item.id === step;
              const done = item.id < step;
              const Icon = item.icon;

              return (
                <div key={item.id} className={`surface-card p-4 transition-all ${active ? 'border-primary/50 ring-2 ring-primary/15' : ''}`}>
                  <div className="flex items-center gap-2.5">
                    <div className={`h-8 w-8 rounded-lg grid place-items-center ${
                      done
                        ? 'bg-success/15 text-success'
                        : active
                          ? 'bg-[image:var(--gradient-primary)] text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                    }`}
                    >
                      {done ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold truncate">{item.title}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{item.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 px-6 lg:px-10 py-8">
        <div className="max-w-5xl mx-auto w-full">
          <div className="surface-card p-6 md:p-8">
            {step === 1 && (
              <OrgProfileStep
                org={org}
                logoFile={logoFile}
                message={orgMessage}
                onChange={updateOrg}
                onLogoChange={setLogoFile}
                onMessage={setOrgMessage}
              />
            )}

            {step === 2 && (
              <DataSourcesStep sources={sources} onToggle={(id) => toggle(id, sources, setSources)} />
            )}

            {step === 3 && (
              <KpiStep kpis={kpis} onToggle={(id) => toggle(id, kpis, setKpis)} />
            )}

            <div className="mt-8 flex items-center justify-between">
              <Button variant="ghost" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              {step < STEPS.length ? (
                <Button
                  onClick={nextStep}
                  disabled={!canAdvance || orgSaving}
                  className="bg-[image:var(--gradient-primary)] text-primary-foreground hover:opacity-90"
                >
                  {orgSaving ? 'Saving...' : 'Continue'} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={finish}
                  disabled={!canAdvance}
                  className="bg-[image:var(--gradient-primary)] text-primary-foreground hover:opacity-90"
                >
                  Launch workspace <CheckCircle2 className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getOnboardingAllocations(account) {
  const pendingAllocations =
    account.org_member?.pending_license_allocation?.allocations ||
    account.pending_license_allocation?.allocations ||
    {};
  const assignedAgents = getAssignedAgents(account);
  const allocations = { ...pendingAllocations, ...assignedAgents };

  const normalized = Object.entries(allocations).reduce((next, [code, seats]) => {
    if (code && isPositiveAgentSeat(seats)) next[code] = Number(seats) || 1;
    return next;
  }, {});

  if (!Object.keys(normalized).length) {
    normalized.image_i = 1;
  }

  return normalized;
}

function OrgProfileStep({ org, logoFile, message, onChange, onLogoChange, onMessage }) {
  const handleLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      onMessage('Supported logo formats are PNG, JPEG and JPG.');
      return;
    }

    if (file.size > 1024 * 1024) {
      onMessage('Maximum logo size is 1MB.');
      return;
    }

    onLogoChange(file);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Organization profile</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Keep your company identity, contact details and industry profile up to date.
        </p>
      </div>

      <div className="rounded-xl border border-border/70 p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="grid h-14 w-14 place-items-center rounded-xl border border-border/70 bg-primary/10">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Branding</h3>
              <p className="text-xs text-muted-foreground">PNG, JPG or JPEG. Maximum 1MB.</p>
              {logoFile && <p className="mt-1 text-xs text-muted-foreground">{logoFile.name}</p>}
            </div>
          </div>
          <label className="inline-flex cursor-pointer items-center justify-center rounded-md border border-[#009b73] bg-[#009b73] px-3 py-2 text-sm font-medium text-white transition-colors hover:border-[#071a2d] hover:bg-[#071a2d] hover:text-[#a7f3d0]">
            <UploadCloud className="mr-2 h-4 w-4" />
            Upload Logo
            <input type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleLogo} />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Organization name">
          <Input placeholder="Enter organization name" value={org.name} onChange={(event) => onChange('name', event.target.value)} />
        </Field>
        <Field label="Industry">
          <Select value={org.industry} onValueChange={(value) => onChange('industry', value)} className="h-10 w-full">
            <SelectItem value="">Select industry</SelectItem>
            {INDUSTRIES.map((industry) => <SelectItem key={industry} value={industry}>{industry}</SelectItem>)}
          </Select>
        </Field>
        <Field label="Business address">
          <Input placeholder="Enter business address" value={org.address} onChange={(event) => onChange('address', event.target.value)} />
        </Field>
        <Field label="City">
          <Input placeholder="Enter city" value={org.city} onChange={(event) => onChange('city', event.target.value)} />
        </Field>
        <Field label="State / Region">
          <Select value={org.state} onValueChange={(value) => onChange('state', value)} className="h-10 w-full">
            <SelectItem value="">Select state</SelectItem>
            {STATES.map((state) => <SelectItem key={state} value={state}>{state}</SelectItem>)}
          </Select>
        </Field>
        <Field label="Primary email">
          <Input type="email" placeholder="Enter email" value={org.email} onChange={(event) => onChange('email', event.target.value)} />
        </Field>
        <Field label="Phone">
          <Input placeholder="Enter phone" value={org.phone} onChange={(event) => onChange('phone', event.target.value)} />
        </Field>
      </div>

      {message && (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {message}
        </div>
      )}
    </div>
  );
}

function DataSourcesStep({ sources, onToggle }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Connect your data sources</h2>
        <p className="text-sm text-muted-foreground mt-1">Select systems IMAGE-I should ingest via API. You can add more later.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SOURCES.map((source) => {
          const checked = sources.includes(source.id);
          return (
            <button
              key={source.id}
              type="button"
              onClick={() => onToggle(source.id)}
              className={`text-left surface-card p-4 hover-lift ${checked ? 'border-primary/50 ring-2 ring-primary/15' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold">{source.name}</p>
                  <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mt-1">{source.category}</p>
                </div>
                <Checkbox checked={checked} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function KpiStep({ kpis, onToggle }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Pick the KPIs to surface</h2>
        <p className="text-sm text-muted-foreground mt-1">These will appear on your overview dashboard.</p>
      </div>
      <div className="space-y-2">
        {KPI_OPTIONS.map((kpi) => {
          const checked = kpis.includes(kpi.id);
          return (
            <button
              key={kpi.id}
              type="button"
              onClick={() => onToggle(kpi.id)}
              className={`w-full text-left flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                checked ? 'border-primary/50 bg-primary/5' : 'border-border hover:bg-muted/40'
              }`}
            >
              <Checkbox checked={checked} />
              <span className="text-sm">{kpi.label}</span>
            </button>
          );
        })}
      </div>
      <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
        <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">Your AI Agent will be tuned automatically based on the KPI groups you select.</p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
